import { useCallback, SetStateAction, useState } from 'react';
import { Dayjs } from 'dayjs';
import { OutgoingCallsRequest } from '../../../../store/reducers/OutgoingCallsSlice';
import { ReportRequest } from '../../../../store/reducers/ReportSlice';
import { useOutgoingHandlersFilter } from './useOutgoingHandlersFilter/useOutgoingHandlersFilter';
import { RangeValue } from './types';
import { formatDate } from '../../../../utils';

interface Props {
  onIndex: (value: SetStateAction<number | undefined>) => void;
  onCallRequest: (value: OutgoingCallsRequest) => void;
  callRequest: OutgoingCallsRequest;
  onCallRequestReport: (value: ReportRequest) => void;
  callRequestReport: ReportRequest;
  onPageSize: (value: SetStateAction<number>) => void;
  total: number;
}

export const useOutgoingCallsHandlers = ({
  onIndex,
  onCallRequest,
  callRequest,
  onCallRequestReport,
  callRequestReport,
  onPageSize,
  total,
}: Props) => {
  const {
    handleClinicsChange,
    handleResultChange,
    handleTaskStatusChange,
    handleAttemptStatusChange,
    handleSpecializationChange,
    handleDoctorsChange,
    handleNotificationTypeChange,
    clinicValue,
    resultValue,
    taskStatusValue,
    attemptStatusValue,
    specializationValue,
    doctorsValue,
    notificationTypeValue,
    attemptTimeRangeValue,
    handleAttemptTimeRangeChange,
    handleOpenClinics,
    handleOpenResult,
    handleOpenTaskStatus,
    handleOpenAttemptStatus,
    handleOpenSpecialization,
    handleOpenDoctors,
    handleOpenNotificationType,
    resetFilters,
    isResetDisabled,
  } = useOutgoingHandlersFilter({
    onCallRequest,
    callRequest,
    onCallRequestReport,
    callRequestReport,
  });

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onCallRequest({
        ...callRequest,
        'Filter.TextSearch': e.target.value,
      });

      onCallRequestReport({
        ...callRequestReport,
        TextSearch: e.target.value,
      });
    },
    [onCallRequest, callRequest, onCallRequestReport, callRequestReport],
  );

  const handleRangePicker = useCallback(
    (values: RangeValue<Dayjs>, formatString: string[]) => {
      formatString &&
        onCallRequest({
          ...callRequest,
          'Filter.From': formatDate(formatString[0]),
          'Filter.To': formatDate(formatString[1]),
        });

      onCallRequestReport({
        ...callRequestReport,
        From: formatDate(formatString[0]),
        To: formatDate(formatString[1]),
      });
    },
    [onCallRequest, callRequest, onCallRequestReport, callRequestReport],
  );

  const handleChange = useCallback(
    (page: number, pageItemCount: number) => {
      onPageSize(pageItemCount);
      onCallRequest({
        ...callRequest,
        PageIndex: page - 1,
        PageSize: pageItemCount,
      });
      onIndex(undefined);
    },
    [onCallRequest, callRequest, onPageSize, onIndex],
  );

  const handlePageSizeChange = useCallback(
    (value: string) => {
      const page =
        callRequest.PageIndex <= Math.floor(total / Number(value))
          ? callRequest.PageIndex
          : Math.floor(total / Number(value));
      onPageSize(Number(value));
      onCallRequest({
        ...callRequest,
        PageIndex: page,
        PageSize: Number(value),
      });
      onIndex(undefined);
    },
    [callRequest, total, onPageSize, onCallRequest, onIndex],
  );

  return {
    handleSearch,
    handleRangePicker,
    handleChange,
    handlePageSizeChange,
    clinicValue,
    handleClinicsChange,
    resultValue,
    handleResultChange,
    taskStatusValue,
    handleTaskStatusChange,
    attemptStatusValue,
    handleAttemptStatusChange,
    specializationValue,
    handleSpecializationChange,
    doctorsValue,
    handleDoctorsChange,
    notificationTypeValue,
    handleNotificationTypeChange,
    attemptTimeRangeValue,
    handleAttemptTimeRangeChange,
    handleOpenClinics,
    handleOpenResult,
    handleOpenTaskStatus,
    handleOpenAttemptStatus,
    handleOpenSpecialization,
    handleOpenDoctors,
    handleOpenNotificationType,
    resetFilters,
    isResetDisabled,
  };
};
